import { useState } from 'react'
import {
  Box,
  Button,
  Card,
  Field,
  Flex,
  Heading,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react'
import { LuMapPin, LuPencil, LuX } from 'react-icons/lu'
import { AxiosError } from 'axios'
import { showSuccess } from '@/lib/alert'
import { useUpdateEventAddress } from '@/hooks/events/useUpdateEventAddress'
import type { ApiErrorResponse } from '@/lib/api'
import type { EventDetail } from '@/services/events'
import BrazilianStateSelect from '@/components/BrazilianStateSelect'

type Props = {
  event: EventDetail
  agendaId: string
  eventId: string
  canManageEvents: boolean
}

const AddressSection = ({ event, agendaId, eventId, canManageEvents }: Props) => {
  const [isEditing, setIsEditing] = useState(false)

  const mutation = useUpdateEventAddress(agendaId!, eventId!)
  const validationErrors = (mutation.error as AxiosError<ApiErrorResponse> | null)?.response?.data
    ?.validation_errors

  const handleCancel = () => {
    mutation.reset()
    setIsEditing(false)
  }

  const handleSubmit = (e: { preventDefault(): void; currentTarget: HTMLFormElement }) => {
    e.preventDefault()
    const data = new FormData(e.currentTarget)

    const payload = {
      state: data.get('state') as string,
      city: (data.get('city') as string).trim(),
      neighborhood: (data.get('neighborhood') as string).trim() || null,
      street: (data.get('street') as string).trim() || null,
      number: (data.get('number') as string).trim() || null,
      complement: (data.get('complement') as string).trim() || null,
    }

    mutation.mutate(payload, {
      onSuccess: () => {
        showSuccess('Endereço atualizado com sucesso!')
        setIsEditing(false)
      },
    })
  }

  return (
    <Card.Root h="full">
      <Card.Body display="flex" flexDirection="column" gap={4}>
        <Flex justify="space-between" align="flex-start">
          <Flex align="center" gap={3}>
            <Box
              bg="blue.subtle"
              color="blue.600"
              p={2}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <LuMapPin size={16} />
            </Box>
            <Box>
              <Heading size="sm" fontWeight="semibold">
                Localização
              </Heading>
              <Text fontSize="xs" color="fg.muted">
                Endereço e localização do evento
              </Text>
            </Box>
          </Flex>
          {!isEditing && canManageEvents && (
            <Button size="xs" variant="ghost" onClick={() => setIsEditing(true)} aria-label="Editar">
              <LuPencil />
            </Button>
          )}
        </Flex>

        {isEditing ? (
          <form onSubmit={handleSubmit}>
            <Stack gap={4}>
              <Flex gap={3} flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                <Field.Root required invalid={!!validationErrors?.state} flexShrink={0} w={{ base: 'full', sm: '120px' }}>
                  <Field.Label>
                    Estado (UF)
                    <Field.RequiredIndicator />
                  </Field.Label>
                  <BrazilianStateSelect
                    variant="subtle"
                    name="state"
                    defaultValue={event.address?.state ?? ''}
                  />
                  {validationErrors?.state?.map((error, i) => (
                    <Field.ErrorText key={i}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>

                <Field.Root required invalid={!!validationErrors?.city} flex="1">
                  <Field.Label>
                    Cidade
                    <Field.RequiredIndicator />
                  </Field.Label>
                  <Input
                    name="city"
                    placeholder="Selecione a cidade"
                    variant="subtle"
                    maxLength={100}
                    defaultValue={event.address?.city ?? ''}
                  />
                  {validationErrors?.city?.map((error, i) => (
                    <Field.ErrorText key={i}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>
              </Flex>

              <Field.Root invalid={!!validationErrors?.neighborhood}>
                <Field.Label>Bairro</Field.Label>
                <Input
                  name="neighborhood"
                  placeholder="Ex: Centro"
                  variant="subtle"
                  maxLength={100}
                  defaultValue={event.address?.neighborhood ?? ''}
                />
                {validationErrors?.neighborhood?.map((error, i) => (
                  <Field.ErrorText key={i}>{error}</Field.ErrorText>
                ))}
              </Field.Root>

              <Flex gap={3} flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                <Field.Root invalid={!!validationErrors?.street} flex="1">
                  <Field.Label>Rua</Field.Label>
                  <Input
                    name="street"
                    placeholder="Ex: Av. Paulista"
                    variant="subtle"
                    maxLength={150}
                    defaultValue={event.address?.street ?? ''}
                  />
                  {validationErrors?.street?.map((error, i) => (
                    <Field.ErrorText key={i}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>

                <Field.Root invalid={!!validationErrors?.number} flexShrink={0} w={{ base: 'full', sm: '100px' }}>
                  <Field.Label>Número</Field.Label>
                  <Input
                    name="number"
                    placeholder="Ex: 123"
                    variant="subtle"
                    maxLength={20}
                    defaultValue={event.address?.number ?? ''}
                  />
                  {validationErrors?.number?.map((error, i) => (
                    <Field.ErrorText key={i}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>
              </Flex>

              <Field.Root invalid={!!validationErrors?.complement}>
                <Field.Label>Complemento</Field.Label>
                <Input
                  name="complement"
                  placeholder="Ex: Apto 42 (opcional)"
                  variant="subtle"
                  maxLength={100}
                  defaultValue={event.address?.complement ?? ''}
                />
                {validationErrors?.complement?.map((error, i) => (
                  <Field.ErrorText key={i}>{error}</Field.ErrorText>
                ))}
              </Field.Root>

              <Flex gap={2} justify="flex-end">
                <Button variant="outline" size="sm" onClick={handleCancel} disabled={mutation.isPending}>
                  <LuX />
                  Cancelar
                </Button>
                <Button type="submit" colorPalette="primary" size="sm" loading={mutation.isPending}>
                  Salvar
                </Button>
              </Flex>
            </Stack>
          </form>
        ) : (
          <Flex direction="column" justifyContent="space-between" gap={3} flex="1">
            {event.address?.static_map_url ? (
              <Box borderRadius="lg" overflow="hidden">
                <img
                  src={event.address.static_map_url}
                  alt="Mapa do local"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '250px',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              </Box>
            ) : (
              <Box
                bg="bg.subtle"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="fg.subtle"
              >
                <LuMapPin size={36} />
              </Box>
            )}

            <Box>
              <Text
                fontSize="xs"
                color="fg.muted"
                fontWeight="medium"
                textTransform="uppercase"
                letterSpacing="wide"
                mb={1}
              >
                Endereço Completo
              </Text>
              <Flex align="center" gap={2}>
                <Box color="blue.500" mt="2px" flexShrink={0}>
                  <LuMapPin size={14} />
                </Box>
                {event.address ? (
                  <Text fontSize="md" fontWeight="semibold">{event.address.full_address}</Text>
                ) : (
                  <Text fontSize="sm" color="fg.muted">
                    Endereço não cadastrado.
                  </Text>
                )}
              </Flex>
            </Box>
          </Flex>
        )}
      </Card.Body>
    </Card.Root>
  )
}

export default AddressSection
